// @ts-nocheck
import React, { useState, useMemo } from 'react';
import {
  Brain, TrendingUp, Target, Lightbulb, AlertTriangle, ArrowRight, BarChart3, Users, Zap, ThumbsUp, ThumbsDown,
  MessageSquare, Activity
} from 'lucide-react';
import { tone, toneForStatus } from "components/ui/tone";
import { SelectField } from "components/ui/select-field";

// Individual Decision Component
export const AIDecisionCard = ({ decision, onApprove, onReject, onViewDetails }) => {
  const [showReasoning, setShowReasoning] = useState(false);

  const getDecisionTypeIcon = (type) => {
    switch (type) {
      case 'create_test': return Target;
      case 'optimize_campaign': return TrendingUp;
      case 'generate_variants': return Lightbulb;
      case 'analyze_results': return BarChart3;
      case 'audience_targeting': return Users;
      case 'automation_trigger': return Zap;
      default: return Brain;
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.9) return 'text-green-600 dark:text-green-300 bg-green-100 dark:bg-green-900/30';
    if (confidence >= 0.7) return 'text-blue-600 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30';
    if (confidence >= 0.5) return 'text-yellow-600 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30';
    return 'text-red-600 dark:text-red-300 bg-red-100 dark:bg-red-900/30';
  };

  const getStatusColor = (status) => tone(toneForStatus(status)).chip;

  const DecisionIcon = getDecisionTypeIcon(decision.type);
  const confidenceColor = getConfidenceColor(decision.confidence);
  const statusColor = getStatusColor(decision.status);

  return (
    <div className="border border-line rounded-lg p-4 ">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
            <DecisionIcon className="w-5 h-5 text-purple-600 dark:text-purple-300" />
          </div>
          <div>
            <h4 className="font-medium text-content">{decision.title}</h4>
            <p className="text-sm text-content-secondary">{decision.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${confidenceColor}`}>
            {Math.round(decision.confidence * 100)}% confident
          </span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>
            {decision.status}
          </span>
        </div>
      </div>

      <div className="space-y-3">
        {/* Decision Summary */}
        <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
          <h5 className="font-medium text-content mb-2">Recommendation</h5>
          <p className="text-sm text-gray-700 dark:text-gray-300">{decision.recommendation}</p>
        </div>

        {/* Expected Impact */}
        {decision.expectedImpact && (
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
              <div className="text-sm text-blue-600 dark:text-blue-300 mb-1">Expected Improvement</div>
              <div className="font-semibold text-blue-900 dark:text-blue-300">
                +{decision.expectedImpact.improvement}%
              </div>
              <div className="text-xs text-blue-600 dark:text-blue-300">
                {decision.expectedImpact.metric}
              </div>
            </div>
            <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3">
              <div className="text-sm text-green-600 dark:text-green-300 mb-1">Success Probability</div>
              <div className="font-semibold text-green-900 dark:text-green-300">
                {Math.round(decision.expectedImpact.probability * 100)}%
              </div>
              <div className="text-xs text-green-600 dark:text-green-300">
                Based on historical data
              </div>
            </div>
          </div>
        )}

        {/* Reasoning Toggle */}
        <div className="flex items-center justify-between pt-2 border-t border-line">
          <button
            onClick={() => setShowReasoning(!showReasoning)}
            className="flex items-center gap-2 text-sm text-content-secondary hover:text-gray-800 dark:hover:text-gray-200"
          >
            <MessageSquare className="w-4 h-4" />
            {showReasoning ? 'Hide Reasoning' : 'Show Reasoning'}
          </button>
          
          {decision.status === 'pending' && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => onReject(decision)}
                className="flex items-center gap-1 px-3 py-1 text-sm text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
              >
                <ThumbsDown className="w-4 h-4" />
                Reject
              </button>
              <button
                onClick={() => onApprove(decision)}
                className="flex items-center gap-1 px-3 py-1 text-sm text-green-600 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg"
              >
                <ThumbsUp className="w-4 h-4" />
                Approve
              </button>
            </div>
          )}
        </div>

        {/* Detailed Reasoning */}
        {showReasoning && (
          <div className="space-y-3 pt-3 border-t border-line">
            <div>
              <h6 className="font-medium text-content mb-2">AI Reasoning</h6>
              <p className="text-sm text-gray-700 dark:text-gray-300">{decision.reasoning}</p>
            </div>

            {/* Supporting Data */}
            {decision.supportingData && (
              <div>
                <h6 className="font-medium text-content mb-2">Supporting Evidence</h6>
                <div className="space-y-2">
                  {decision.supportingData.historicalPerformance && (
                    <div className="flex items-center gap-2 text-sm">
                      <BarChart3 className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                      <span className="text-content-secondary">Historical performance:</span>
                      <span className="font-medium text-content">
                        {decision.supportingData.historicalPerformance}% improvement
                      </span>
                    </div>
                  )}
                  {decision.supportingData.similarTestResults && (
                    <div className="flex items-center gap-2 text-sm">
                      <Target className="w-4 h-4 text-green-600 dark:text-green-300" />
                      <span className="text-content-secondary">Similar tests:</span>
                      <span className="font-medium text-content">
                        {decision.supportingData.similarTestResults.length} successful cases
                      </span>
                    </div>
                  )}
                  {decision.supportingData.statisticalBasis && (
                    <div className="flex items-center gap-2 text-sm">
                      <Activity className="w-4 h-4 text-purple-600 dark:text-purple-300" />
                      <span className="text-content-secondary">Statistical basis:</span>
                      <span className="font-medium text-content">
                        {decision.supportingData.statisticalBasis}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Agent Information */}
            <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Brain className="w-4 h-4 text-purple-600 dark:text-purple-300" />
                <span className="font-medium text-purple-900 dark:text-purple-300">Agent: {decision.agentName}</span>
              </div>
              <div className="text-sm text-purple-700 dark:text-purple-300">
                Specialization: {decision.agentSpecialization}
              </div>
              <div className="text-xs text-purple-600 dark:text-purple-300 mt-1">
                Decision made at {new Date(decision.timestamp).toLocaleString()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Agent Collaboration Timeline Component
export const AgentCollaborationTimeline = ({ collaborations = [] }) => {
  const sortedCollaborations = useMemo(() => {
    return [...collaborations].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [collaborations]);

  if (sortedCollaborations.length === 0) {
    return (
      <div className="text-center py-6 text-content-muted">
        <Brain className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        <p>No agent collaborations yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {sortedCollaborations.map((collaboration, index) => (
        <div key={collaboration.id} className="relative">
          {/* Timeline Line */}
          {index < sortedCollaborations.length - 1 && (
            <div className="absolute left-6 top-12 w-0.5 h-16 bg-gray-200 dark:bg-dark-600"></div>
          )}
          
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
              <Brain className="w-6 h-6 text-purple-600 dark:text-purple-300" />
            </div>
            
            <div className="flex-1 bg-surface border border-line rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-content">{collaboration.objective}</h4>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  collaboration.status === 'completed' 
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                    : collaboration.status === 'executing'
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                }`}>
                  {collaboration.status}
                </span>
              </div>
              
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-content-secondary" />
                <span className="text-sm text-content-secondary">
                  {collaboration.participatingAgents.length} agents collaborating
                </span>
                <span className="text-xs text-content-muted">
                  • {new Date(collaboration.timestamp).toLocaleDateString()}
                </span>
              </div>

              {/* Participating Agents */}
              <div className="flex items-center gap-2 mb-3">
                {collaboration.participatingAgents.map((agentId, idx) => (
                  <div key={agentId} className="flex items-center gap-1">
                    {idx > 0 && <ArrowRight className="w-3 h-3 text-content-subtle" />}
                    <div className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-1 rounded-full text-xs font-medium">
                      Agent {agentId.slice(-4)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Results Summary */}
              {collaboration.results && (
                <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
                  <h5 className="font-medium text-content mb-2">Collaboration Results</h5>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{collaboration.results.summary}</p>
                  {collaboration.results.metrics && (
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <span className="text-content-secondary">
                        Decisions Made: <span className="font-medium">{collaboration.results.metrics.decisionsCount}</span>
                      </span>
                      <span className="text-content-secondary">
                        Success Rate: <span className="font-medium">{collaboration.results.metrics.successRate}%</span>
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Agent Performance Insights Component
export const AgentPerformanceInsights = ({ agent, recentDecisions = [] }) => {
  const performanceMetrics = useMemo(() => {
    const approvedDecisions = recentDecisions.filter(d => d.status === 'approved');
    const implementedDecisions = recentDecisions.filter(d => d.status === 'implemented');
    
    const avgConfidence = recentDecisions.length > 0
      ? recentDecisions.reduce((sum, d) => sum + d.confidence, 0) / recentDecisions.length
      : 0;

    const successRate = implementedDecisions.length > 0
      ? (implementedDecisions.filter(d => d.actualImpact > 0).length / implementedDecisions.length) * 100
      : 0;

    return {
      totalDecisions: recentDecisions.length,
      approvedDecisions: approvedDecisions.length,
      implementedDecisions: implementedDecisions.length,
      avgConfidence: avgConfidence,
      successRate: successRate,
      approvalRate: recentDecisions.length > 0 ? (approvedDecisions.length / recentDecisions.length) * 100 : 0
    };
  }, [recentDecisions]);

  return (
    <div className="bg-surface border border-line rounded-lg p-4">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
          <Brain className="w-6 h-6 text-purple-600 dark:text-purple-300" />
        </div>
        <div>
          <h3 className="font-medium text-content">{agent.name}</h3>
          <p className="text-sm text-content-secondary">{agent.specialization} Specialist</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-content">{performanceMetrics.totalDecisions}</div>
          <div className="text-xs text-content-muted">Total Decisions</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-300">{Math.round(performanceMetrics.avgConfidence * 100)}%</div>
          <div className="text-xs text-content-muted">Avg Confidence</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600 dark:text-green-300">{Math.round(performanceMetrics.approvalRate)}%</div>
          <div className="text-xs text-content-muted">Approval Rate</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-300">{Math.round(performanceMetrics.successRate)}%</div>
          <div className="text-xs text-content-muted">Success Rate</div>
        </div>
      </div>

      {/* Recent Decision Types */}
      <div className="space-y-2">
        <h4 className="font-medium text-content">Recent Decision Types</h4>
        <div className="flex flex-wrap gap-2">
          {[...new Set(recentDecisions.map(d => d.type))].map(type => (
            <span key={type} className="px-2 py-1 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 rounded-full text-xs">
              {type.replace('_', ' ')}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// Main AI Agent Decision Trail Component
export const AIAgentDecisionTrail = ({ 
  campaign, 
  decisions = [], 
  collaborations = [],
  agents = [],
  onApproveDecision,
  onRejectDecision,
  onViewDecisionDetails
}) => {
  const [activeTab, setActiveTab] = useState('decisions');
  const [filterStatus, setFilterStatus] = useState('all');

  // Filter decisions based on campaign and status
  const filteredDecisions = useMemo(() => {
    let filtered = decisions.filter(decision => 
      decision.campaignId === campaign.id || decision.templateId === campaign.templateId
    );

    if (filterStatus !== 'all') {
      filtered = filtered.filter(decision => decision.status === filterStatus);
    }

    return filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [decisions, campaign.id, campaign.templateId, filterStatus]);

  // Get campaign-related collaborations
  const campaignCollaborations = collaborations.filter(collab =>
    collab.campaignId === campaign.id || collab.templateId === campaign.templateId
  );

  // Get involved agents
  const involvedAgents = agents.filter(agent =>
    filteredDecisions.some(decision => decision.agentId === agent.id)
  );

  const pendingDecisions = filteredDecisions.filter(d => d.status === 'pending');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-content">AI Decision Trail</h3>
          <p className="text-sm text-content-secondary">
            Track AI agent decisions and reasoning for {campaign.name}
          </p>
        </div>
        {pendingDecisions.length > 0 && (
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-300" />
            <span className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
              {pendingDecisions.length} decision{pendingDecisions.length !== 1 ? 's' : ''} awaiting approval
            </span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-line">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('decisions')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'decisions'
                ? 'border-purple-500 text-purple-600 dark:text-purple-300'
                : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300 hover:border-line-strong'
            }`}
          >
            Decisions ({filteredDecisions.length})
          </button>
          <button
            onClick={() => setActiveTab('collaborations')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'collaborations'
                ? 'border-purple-500 text-purple-600 dark:text-purple-300'
                : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300 hover:border-line-strong'
            }`}
          >
            Collaborations ({campaignCollaborations.length})
          </button>
          <button
            onClick={() => setActiveTab('agents')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'agents'
                ? 'border-purple-500 text-purple-600 dark:text-purple-300'
                : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300 hover:border-line-strong'
            }`}
          >
            Agent Performance ({involvedAgents.length})
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'decisions' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter by status:</label>
            <SelectField
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-1 border border-line-strong rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Decisions</option>
              <option value="pending">Pending Approval</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="implemented">Implemented</option>
            </SelectField>
          </div>

          {/* Decisions List */}
          <div className="space-y-4">
            {filteredDecisions.length > 0 ? (
              filteredDecisions.map(decision => (
                <AIDecisionCard
                  key={decision.id}
                  decision={decision}
                  onApprove={onApproveDecision}
                  onReject={onRejectDecision}
                  onViewDetails={onViewDecisionDetails}
                />
              ))
            ) : (
              <div className="text-center py-8 text-content-muted">
                <Brain className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No AI decisions found for this campaign</p>
                <p className="text-sm">Decisions will appear here as AI agents analyze and optimize your campaign</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'collaborations' && (
        <AgentCollaborationTimeline collaborations={campaignCollaborations} />
      )}

      {activeTab === 'agents' && (
        <div className="space-y-4">
          {involvedAgents.length > 0 ? (
            involvedAgents.map(agent => (
              <AgentPerformanceInsights
                key={agent.id}
                agent={agent}
                recentDecisions={filteredDecisions.filter(d => d.agentId === agent.id)}
              />
            ))
          ) : (
            <div className="text-center py-8 text-content-muted">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No AI agents involved in this campaign yet</p>
              <p className="text-sm">Enable AI features to see agent performance insights</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIAgentDecisionTrail;